import { Link } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
import type { MenuSection, NavItem } from './useNavItems';
import { cn } from '@/lib/utils';
import { ModeToggle } from '@/components/mode-toggle';

export function Topbar({
  items,
  title,
  onAction,
  currentPath,
}: {
  items: MenuSection[];
  title?: string;
  onAction?: (key: string) => boolean | void;
  currentPath?: string;
}) {
  const [open, setOpen] = useState(false);
  const firstLinkRef = useRef<HTMLAnchorElement | null>(null);
  const menuId = 'mobile-menu';

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    // focus first item for accessibility
    setTimeout(() => firstLinkRef.current?.focus(), 0);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const Item = ({ item, index }: { item: NavItem; index: number }) => {
    const method = item.method ?? 'get';
    const isActive = method === 'get' && currentPath === item.href;
    return (
      <Link
        href={item.href}
        method={method}
        className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm hover:bg-muted/40 cursor-pointer"
        onClick={(e) => {
          const intercepted = onAction && onAction(item.key);
          if (intercepted) e.preventDefault();
          setOpen(false);
        }}
        role="menuitem"
        aria-current={isActive ? 'page' : undefined}
        ref={index === 0 ? firstLinkRef : undefined}
      >
        {item.icon && <item.icon className="w-4 h-4 opacity-80" />}
        <span>{item.label}</span>
      </Link>
    );
  };

  const topItems = items.flatMap((section) => (!section.pullDown ? section.items : []));
  const bottomItems = items.flatMap((section) => (section.pullDown ? section.items : []));

  return (
    <header className="sticky top-0 z-40 border-b bg-background/60 backdrop-blur">
      <div className="flex items-center justify-between px-3 py-2">
        <button
          aria-label={open ? 'Chiudi menu' : 'Apri menu'}
          aria-expanded={open}
          aria-controls={menuId}
          aria-haspopup="menu"
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border px-2 py-1 text-sm hover:bg-muted"
        >
          ☰
        </button>
        <div className="text-sm font-semibold opacity-80">{title ?? 'Emovia'}</div>
        <ModeToggle />
      </div>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Menu di navigazione"
          className={cn(
            'absolute inset-x-0 top-12 z-40 border-b bg-background/95 backdrop-blur shadow',
          )}
        >
          <div className="p-3 space-y-2">
            {topItems.map((i, idx) => (
              <Item key={`${i.label}-${i.href}`} item={i} index={idx} />
            ))}
            <div className="my-2 h-px bg-border" />
            {bottomItems.map((i, idx) => (
              <Item key={`${i.label}-${i.href}`} item={i} index={idx + items.length} />
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
